/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { MediaPowderBatchInfo, ValidationErrors } from "../../../types/mediaBatchPreparation.types";
import { MediaPreparation } from "../../../types/mediaBatchCommon.types";

interface Props {
	mixingParameters: any;
	setMixingParameters: React.Dispatch<React.SetStateAction<any>>;
	batchInfo: MediaPowderBatchInfo | null;
	mediaPreparationData: MediaPreparation | undefined;
	errors: ValidationErrors;
	setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
}

const MediaProcessParameters = ({
	mixingParameters,
	setMixingParameters,
	batchInfo,
	mediaPreparationData,
	errors,
	setErrors,

}: Props) => {
	const { t } = useTranslation();

	// Use the passed prop as the single source of truth for read-only mode
	const isReadOnly = !!mediaPreparationData;

	const handleChange = (key: string, value: string) => {
		setMixingParameters((prev: any) => {
			const updated = { ...prev, [key]: value };

			if (key === "process_start_time" || key === "process_end_time") {
				const startMs = new Date(updated.process_start_time).getTime();
				const endMs = new Date(updated.process_end_time).getTime();

				updated.stirring_duration_minutes =
					startMs && endMs && endMs > startMs
						? String(Math.round((endMs - startMs) / 60000))
						: "";
			}

			return updated;
		});

		setErrors((prev) => ({
			...prev,
			...(key === "process_start_time" && { startTime: "" }),
			...(key === "process_end_time" && { endTime: "" }),
			...(key === "observed_ph" && { observedPh: "" }),
		}));
	};

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
			<div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
				<div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
					04
				</div>
				<h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
					{t("mediaBatchInit.preparation.processParameters")}
				</h3>
			</div>
			<div className="p-8 space-y-10">
				<div className="grid grid-cols-4 gap-6">
					{/* Start Time */}
					<div className="space-y-2">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.preparation.startTime")}  <span className="text-red-500">*</span>
						</label>
						<input
							type="datetime-local"
							value={mediaPreparationData?.start_time ?? mixingParameters.process_start_time ?? ""}
							readOnly={isReadOnly}
							disabled={isReadOnly}
							onChange={(e) => handleChange("process_start_time", e.target.value)}
							className={`w-full px-4 py-2 bg-[#F9FAFB] border border-slate-200 rounded-lg text-xs text-slate-700 outline-none focus:border-orange-500 ${
								isReadOnly ? "cursor-not-allowed opacity-70 bg-slate-100" : ""
							}`}
						/>
						{errors.startTime && <p className="text-[11px] text-red-500">{errors.startTime}</p>}
					</div>

					{/* End Time */}
					<div className="space-y-2">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.preparation.endTime")}  <span className="text-red-500">*</span>
						</label>
						<input
							type="datetime-local"
							value={mediaPreparationData?.end_time ?? mixingParameters.process_end_time ?? ""}
							readOnly={isReadOnly}
							disabled={isReadOnly}
							onChange={(e) => handleChange("process_end_time", e.target.value)}
							className={`w-full px-4 py-2 bg-[#F9FAFB] border border-slate-200 rounded-lg text-xs text-slate-700 outline-none focus:border-orange-500 ${
								isReadOnly ? "cursor-not-allowed opacity-70 bg-slate-100" : ""
							}`}
						/>
						{errors.endTime && <p className="text-[11px] text-red-500">{errors.endTime}</p>}
					</div>

					{/* Stirring Duration — read-only, derived */}
					<div className="space-y-2">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.preparation.stirringMin")}
						</label>
						<input
							type="text"
							readOnly
							disabled
							value={(() => {
								const val = mediaPreparationData?.stirring_time_min ?? mixingParameters.stirring_duration_minutes;
								return val ? `${val} min` : "";
							})()}
							placeholder="—"
							className="w-full px-4 py-2 bg-slate-100 border border-dashed border-slate-300 rounded-lg text-xs font-bold text-slate-400 outline-none cursor-not-allowed"
						/>
					</div>

					{/* Agitation RPM */}
					<div className="space-y-2">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
							{t("mediaBatchInit.preparation.agitationRPM")}  
						</label>
						<input
							type="number"
							value={mediaPreparationData?.agitation_rpm ?? mixingParameters.agitation_rpm ?? ""}
							readOnly={isReadOnly}
							disabled={isReadOnly}
							onChange={(e) => handleChange("agitation_rpm", e.target.value)}
							className={`w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:border-orange-500 ${
								isReadOnly ? "cursor-not-allowed opacity-70 bg-slate-100" : ""
							}`}
						/>
					</div>
				</div>

				<div className="grid grid-cols-12 gap-8 items-stretch">
					{/* Observed pH */}
					<div className="col-span-12 lg:col-span-4 flex flex-col items-center justify-center p-8 bg-slate-50 border border-slate-100 rounded-2xl">
						<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">
							{t("mediaBatchInit.preparation.finalObservedPhRecording")}  <span className="text-red-500">*</span>
						</p>
						<div className="bg-white border-2 border-slate-200 rounded-2xl px-10 py-4 text-center">
							<input
								type="number"
								value={mediaPreparationData?.observed_final_ph ?? mixingParameters.observed_ph ?? ""}
								readOnly={isReadOnly}
								disabled={isReadOnly}
								onChange={(e) => handleChange("observed_ph", e.target.value)}
								placeholder="0.0"
								className={`text-5xl font-black text-[#2D3E50] w-full text-center bg-transparent outline-none border-none ${
									isReadOnly ? "cursor-not-allowed opacity-70" : ""
								}`}
							/>
						</div>
						<p className="text-[8px] font-bold text-slate-400 uppercase mt-4">
							{t("mediaBatchInit.preparation.target")}: {batchInfo?.final_ph} ± {batchInfo?.final_ph_tolerance} @ {batchInfo?.storage_temperature}
						</p>
						{errors.observedPh && <p className="text-[11px] text-red-500">{errors.observedPh}</p>}
					</div>

					{/* Process Remarks */}
					<div className="col-span-12 lg:col-span-8 flex flex-col">
						<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
							{t("mediaBatchInit.preparation.processRemarks")}
						</label>
						<textarea
							value={mediaPreparationData?.remarks ?? mixingParameters.process_remarks ?? ""}
							onChange={(e) => handleChange("process_remarks", e.target.value)}
							readOnly={isReadOnly}
							disabled={isReadOnly}
							placeholder="Enter any technical observations..."
							className={`flex-1 w-full bg-[#F9FAFB] border border-slate-200 rounded-2xl p-6 text-xs font-medium text-slate-600 outline-none focus:border-orange-500 resize-none ${
								isReadOnly ? "cursor-not-allowed opacity-70 bg-slate-100" : ""
							}`}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MediaProcessParameters;
